/**
 * A part of a machine (an extractor): one instanced mesh of the props, placed by its offset in
 * machine space (y up from the tile's top, a tile about 1.2 wide), perhaps turning or glowing. The
 * helpers make parts the same way for every machine.
 */
import type * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

export type SpinAxis = 'x' | 'y' | 'z';

export interface MachinePart {
  /** Unique across every machine: one instanced mesh each. */
  name: string;
  /** Centred on its own origin; `offset` places it. */
  geometry: THREE.BufferGeometry;
  color: number;
  roughness: number;
  metalness: number;
  offset: [number, number, number];
  /** Turns while the machine works, around its own centre: the axis and radians per second. */
  spin: SpinAxis | null;
  speed: number;
  shadow: boolean;
  /** A light of its own, this colour (a core, a tip, crystals). */
  glow?: number;
  /** How strong that light is: 0.9 unless said (the stonecutter's chips match its lit stones). */
  glowStrength?: number;
  /** Its light swells and fades (the stylized look). */
  pulse?: boolean;
}

/** A part with the usual defaults: a little rough, a little metal, standing still, casting shadow. */
export const part = (
  name: string,
  geometry: THREE.BufferGeometry,
  color: number,
  offset: [number, number, number],
  extra: Partial<
    Pick<
      MachinePart,
      'roughness' | 'metalness' | 'spin' | 'speed' | 'glow' | 'glowStrength' | 'pulse'
    >
  > = {},
): MachinePart => ({
  name,
  geometry,
  color,
  offset,
  roughness: 0.55,
  metalness: 0.2,
  spin: null,
  speed: 0,
  shadow: true,
  ...extra,
});

/** Copies of `make()` turned round the y axis at `angles`, `at` out from the middle, as one shape. */
export function around(
  make: (k: number) => THREE.BufferGeometry,
  angles: readonly number[],
  at: number,
): THREE.BufferGeometry {
  const parts = angles.map((a, k) => make(k).translate(at, 0, 0).rotateY(a));
  const merged = mergeGeometries(parts);
  for (const p of parts) p.dispose();
  return merged;
}

/** Shapes as one: they keep their own places. */
export function merged(shapes: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const out = mergeGeometries(shapes);
  for (const s of shapes) s.dispose();
  return out;
}
